import React from "react";
import { useTranslation } from "react-i18next";

const prettifyCode = (code = "") =>
  String(code || "")
    .replace(/^course_intro_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const OPTIONAL_REVIEW_ITEM_PATTERN = /summary slide|key tips|local support|support service/i;
const isOptionalReadinessItem = (item) =>
  item?.optional === true ||
  OPTIONAL_REVIEW_ITEM_PATTERN.test(item?.label || item || "");

const ReadinessItem = ({
  ok,
  title,
  items = [],
  optionalItems = [],
  optionalLabel = "Optional",
}) => (
  <div
    className={`lms-readiness-item ${
      ok ? "lms-readiness-item-success" : "lms-readiness-item-error"
    }`}
  >
    <div className="lms-readiness-item-left">
      <span className="lms-readiness-item-icon">
        <i className={`fas ${ok ? "fa-check-circle" : "fa-times-circle"}`} />
      </span>

      <div className="lms-readiness-item-content">
        <h6>{title}</h6>
        {!ok && items.length > 0 && (
          <ul className="lms-readiness-missing-list">
            {items.map((item) => (
              <li key={`${item.code}-${item.label}`}>{item.label}</li>
            ))}
          </ul>
        )}
        {optionalItems.length > 0 && (
          <ul className="lms-readiness-missing-list">
            {optionalItems.map((item) => (
              <li key={`optional-${item.code}-${item.label}`}>
                {item.label} ({optionalLabel})
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </div>
);

const LmsReadinessPanel = ({
  readiness,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  if (!readiness) return null;

  const c = readiness.checks || {};
  const courseItems = readiness.courseItems || [];
  const moduleItems = readiness.modules || c.modules || [];
  const courseReady = courseItems.length
    ? courseItems.every((item) => item.complete)
    : !!c.courseInfo && !!c.introVideoESHS && !!c.introVideoSSHS && !!c.hasModules;

  return (
    <div className="lms-readiness-panel">
      <div className="lms-readiness-layout">
        <div className="lms-readiness-details">
          {!hideHeader && (
            <div className="lms-readiness-header">
              <div>
                <h4>{t("admin.lms.readiness.title", "Readiness")}</h4>
                <p>
                  {t(
                    "admin.lms.readiness.subtitle",
                    "Review all required items before publishing."
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="lms-readiness-list">
            <ReadinessItem
              ok={courseReady}
              title={t("admin.lms.readiness.courseItems", "Course setup")}
              items={courseItems.filter((item) => !item.complete)}
            />

            {moduleItems.map((moduleItem, index) => {
              const missingItems =
                moduleItem.remainingItems ||
                (moduleItem.issues || []).map((label, issueIndex) => ({
                  code: `${moduleItem.moduleId || index}-${issueIndex}`,
                  label,
                }));
              const requiredMissingItems = (missingItems || []).filter(
                (item) => !isOptionalReadinessItem(item)
              );
              const optionalMissingItems =
                moduleItem.optionalRemainingItems ||
                (missingItems || []).filter(isOptionalReadinessItem);
              const moduleReady = moduleItem.ready || requiredMissingItems.length === 0;

              return (
                <ReadinessItem
                  key={moduleItem.moduleId || moduleItem.title || index}
                  ok={moduleReady}
                  title={moduleItem.title || prettifyCode(moduleItem.code)}
                  items={requiredMissingItems}
                  optionalItems={optionalMissingItems}
                  optionalLabel={t("common.optional", "Optional")}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LmsReadinessPanel;
